import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSpinRetentionGiftMutation } from '../../../features/customerApiSlice';
import OmegaImage from '../../../assets/omega3/1.webp';
import { GiftBox, GiftStatusIcon, PrizeWheel } from './RetentionGiftVisuals';
import RetentionGiftTimeline from './RetentionGiftTimeline';
import RetentionGiftConfetti from './RetentionGiftConfetti';
import { getBrowserLocale } from '../../../utils/language';

const RetentionGiftWheel = ({ gift: serverGift }) => {
  const { t, i18n } = useTranslation();
  const [spinGift, { isLoading, data: spinResult }] = useSpinRetentionGiftMutation();
  const [isSpinning, setIsSpinning] = useState(false);
  const [revealedSubscriptionId, setRevealedSubscriptionId] = useState(null);
  const wheelRef = useRef(null);
  const animationRef = useRef(null);
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      animationRef.current?.cancel();
    };
  }, []);

  const gift =
    serverGift?.status === 'unlocked' &&
    spinResult?.gift?.userSubscriptionId === serverGift.userSubscriptionId
      ? spinResult.gift
      : serverGift;
  if (!gift || !['teaser', 'unlocked', 'reserved'].includes(gift.status)) return null;
  const locale = getBrowserLocale(i18n.language);
  const formatDate = (value, options) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(locale, { timeZone: 'Europe/Berlin', ...options });
  };
  const targetMonth = formatDate(gift.scheduledRenewalAt, { month: 'long' });
  const targetDate = formatDate(gift.scheduledRenewalAt, { day: 'numeric', month: 'long', year: 'numeric' });
  const isReserved = gift.status === 'reserved' && !isSpinning;
  const hasJustSpun = isReserved && revealedSubscriptionId === gift.userSubscriptionId;
  const isTeaser = gift.status === 'teaser';

  const handleSpin = async () => {
    if (!gift.canSpin || isSpinning || isLoading) return;
    setIsSpinning(true);
    try {
      const shouldReduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const cycleDuration = 800;
      const waitingAnimation = wheelRef.current?.animate && !shouldReduceMotion
        ? wheelRef.current.animate(
            [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
            { duration: cycleDuration, iterations: Infinity, easing: 'linear' },
          )
        : null;
      animationRef.current = waitingAnimation;
      await spinGift({ userSubscriptionId: gift.userSubscriptionId }).unwrap();
      if (!isMountedRef.current) return;

      if (waitingAnimation) {
        const elapsed = Number(waitingAnimation.currentTime) || 0;
        const angle = (elapsed / cycleDuration) * 360;
        const duration = Math.max(1000, 5000 - elapsed);
        const finalAngle = Math.ceil(angle / 360) * 360 + 1080;
        waitingAnimation.cancel();
        const animation = wheelRef.current.animate(
          [{ transform: `rotate(${angle}deg)` }, { transform: `rotate(${finalAngle}deg)` }],
          { duration, easing: 'cubic-bezier(.12,.72,.08,1)', fill: 'forwards' },
        );
        animationRef.current = animation;
        await animation.finished;
      }
      if (isMountedRef.current) setRevealedSubscriptionId(gift.userSubscriptionId);
    } catch (error) {
      if (isMountedRef.current && error?.name !== 'AbortError') toast.error(t('RetentionGift_Error'));
    } finally {
      animationRef.current?.cancel();
      animationRef.current = null;
      if (isMountedRef.current) setIsSpinning(false);
    }
  };

  const firstDate = formatDate(gift.firstRenewalAt, { day: 'numeric', month: 'short' });
  const shortTargetDate = formatDate(gift.scheduledRenewalAt, { day: 'numeric', month: 'short' });
  const headingClass = `t-mb-1.5 t-mt-2 t-font-serif t-text-[28px] t-font-normal t-leading-[1.08] ${isTeaser ? '!t-text-navy' : '!t-text-white'}`;
  const emphasis = (
    <em
      className={`t-font-serif t-font-normal t-italic ${isTeaser ? 't-text-quiz-teal' : 't-text-butter'}`}
    />
  );

  return (
    <section
      aria-label={t('RetentionGift_Label')}
      aria-busy={isSpinning || isLoading}
      className={`t-relative t-mx-auto lg:t-mx-0 t-w-full t-max-w-[540px] t-overflow-hidden t-rounded-[22px] t-px-5 t-pb-5 t-pt-[22px] t-text-center t-font-sans t-text-base t-font-normal t-leading-[normal] ${isTeaser ? 't-border t-border-solid t-border-[#E3E9E8] t-bg-white t-text-navy' : 't-bg-navy t-text-white'}`}
    >
      {isTeaser ? (
        <>
          <GiftBox />
          <h3 className={headingClass}>
            <Trans i18nKey="RetentionGift_TeaserTitle" components={{ em: emphasis }} />
          </h3>
          <p className="t-mx-auto t-mb-4 t-mt-0 t-max-w-[30ch] t-text-[13.5px] t-leading-[1.45] t-text-[#4A5F66]">
            {targetMonth
              ? t('RetentionGift_TeaserBody', { month: targetMonth })
              : t('RetentionGift_TeaserBodyPending')}
          </p>
          <span className="t-inline-flex t-items-center t-gap-1.5 t-rounded-full t-bg-[#EEF2F1] t-px-3.5 t-py-[7px] t-text-[12.5px] t-font-bold t-leading-[normal]">
            <GiftStatusIcon type="lock" className="t-h-[13px] t-w-[13px]" />
            {firstDate ? t('RetentionGift_UnlockFrom', { date: firstDate }) : t('RetentionGift_FirstRenewal')}
          </span>
          <p className="t-mb-0 t-mt-3 t-text-[12.5px] t-leading-[normal] t-tracking-[0.01em] t-text-[#4A5F66]">
            {t('RetentionGift_ActiveNote')}
          </p>
          <RetentionGiftTimeline firstDate={firstDate} targetDate={shortTargetDate} t={t} />
        </>
      ) : isReserved ? (
        <>
          {hasJustSpun && (
            <div className="t-relative t-mx-auto t-w-full t-max-w-[300px]">
              <PrizeWheel wheelRef={wheelRef} label={(prize) => t(`RetentionGift_Prize_${prize}`)} />
              <RetentionGiftConfetti />
            </div>
          )}
          <div aria-live="polite" aria-atomic="true" className="t-mt-1.5 t-pt-2">
            <div className="t-inline-flex t-items-center t-gap-[7px] t-text-[11px] t-font-bold t-uppercase t-leading-[normal] t-tracking-[0.08em] t-text-butter before:t-h-1.5 before:t-w-1.5 before:t-rounded-full before:t-bg-butter before:t-content-['']">
              {t('RetentionGift_ResultLabel')}
            </div>
            <h3 className={headingClass}>
              <Trans i18nKey="RetentionGift_ResultTitle" components={{ em: emphasis }} />
            </h3>
            <div className="t-my-3.5 t-flex t-items-center t-gap-3.5 t-rounded-2xl t-border t-border-solid t-border-white/[0.18] t-bg-white/10 t-p-3 t-text-left">
              <img
                src={OmegaImage}
                alt="Omega-3"
                className="t-h-[84px] t-w-[84px] t-flex-none t-rounded-xl t-bg-[#eeeeee] t-object-cover"
              />
              <div>
                <b className="t-block t-text-[15px] t-leading-[normal]">{t('RetentionGift_PrizeName')}</b>
                <span className="t-mt-0.5 t-block t-text-[12.5px] t-leading-[1.4] t-opacity-80">
                  {t('RetentionGift_OneBottle')}
                </span>
                <span className="t-sr-only">{t('RetentionGift_Free')}</span>
              </div>
            </div>
            <div className="t-mb-1 t-flex t-items-center t-gap-3 t-rounded-[14px] t-border t-border-solid t-border-white/[0.18] t-bg-white/10 t-px-3.5 t-py-3 t-text-left">
              <span className="t-grid t-h-[30px] t-w-[30px] t-flex-none t-place-items-center t-rounded-full t-bg-butter t-text-navy">
                <GiftStatusIcon type="check" className="t-h-[15px] t-w-[15px]" />
              </span>
              <div>
                <b className="t-block t-text-sm t-leading-[normal]">
                  {targetMonth
                    ? t('RetentionGift_Added', { month: targetMonth })
                    : t('RetentionGift_AddedNext')}
                </b>
                <span className="t-mt-px t-block t-text-[12.5px] t-leading-[normal] t-opacity-[0.78]">
                  {targetDate
                    ? t('RetentionGift_RenewalDate', { date: targetDate })
                    : t('RetentionGift_DatePending')}
                </span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div aria-live="polite" aria-atomic="true">
            <h3 className={headingClass}>
              <Trans i18nKey="RetentionGift_UnlockTitle" components={{ em: emphasis }} />
            </h3>
            <p className="t-mx-auto t-mb-4 t-mt-0 t-max-w-[30ch] t-text-[13.5px] t-leading-[1.45] t-text-white/[0.78]">
              {t('RetentionGift_UnlockBody')}
            </p>
          </div>
          <div className="t-flex t-flex-col t-items-center t-gap-3.5">
            <PrizeWheel wheelRef={wheelRef} label={(prize) => t(`RetentionGift_Prize_${prize}`)} />
            <button
              type="button"
              onClick={handleSpin}
              disabled={!gift.canSpin || isSpinning || isLoading}
              className="t-min-w-[190px] t-rounded-full t-border-0 t-bg-butter t-px-[26px] t-py-3.5 t-text-[15px] t-font-extrabold t-leading-[normal] t-text-navy t-transition-[transform,box-shadow,opacity] hover:t--translate-y-px hover:t-shadow-[0_8px_20px_rgba(0,0,0,0.22)] active:t-scale-[0.97] focus-visible:t-outline focus-visible:t-outline-2 focus-visible:t-outline-offset-[3px] focus-visible:t-outline-white disabled:t-cursor-default disabled:t-opacity-55 disabled:hover:t-translate-y-0 disabled:hover:t-shadow-none"
            >
              {t(isSpinning || isLoading ? 'RetentionGift_Spinning' : 'RetentionGift_Spin')}
            </button>
          </div>
          <p className="t-mb-0 t-mt-3 t-text-[12.5px] t-leading-[normal] t-text-white/70">
            {targetDate
              ? t('RetentionGift_RenewalDate', { date: targetDate })
              : t('RetentionGift_DatePending')}
          </p>
          <p className="t-sr-only">{t('RetentionGift_Expiry')}</p>
        </>
      )}
      {gift.isPaused && (
        <p className={`t-mb-0 t-mt-3 t-text-[12.5px] ${isTeaser ? 't-text-[#4A5F66]' : 't-text-butter'}`}>
          {t('RetentionGift_Paused')}
        </p>
      )}
    </section>
  );
};

export default RetentionGiftWheel;
